import { useState, useEffect, useCallback, useRef } from 'react';
import { MarketingEmailSendStatusEvent } from '../api/generated/model';

export interface UseMarketingEmailSendEventsOptions {
  cleanupDelayMs?: number;
}

export interface UseMarketingEmailSendEventsReturn {
  activeMarketingEmailSends: Map<string, MarketingEmailSendStatusEvent>;
  handleMarketingEmailSendEvent: (event: MarketingEmailSendStatusEvent) => void;
}

// Tracks in-flight marketing email sends so MarketingEmailSendButton can
// render progress/terminal state from the same Map<send_id, event> pattern
// useSyncEvents uses for sync_id.
export function useMarketingEmailSendEvents(
  options: UseMarketingEmailSendEventsOptions = {}
): UseMarketingEmailSendEventsReturn {
  const { cleanupDelayMs = 5000 } = options;

  const [activeMarketingEmailSends, setActiveMarketingEmailSends] = useState<Map<string, MarketingEmailSendStatusEvent>>(new Map());
  const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleCleanup = useCallback((sendId: string) => {
    const existing = cleanupTimersRef.current.get(sendId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      setActiveMarketingEmailSends(prev => {
        const next = new Map(prev);
        next.delete(sendId);
        return next;
      });
      cleanupTimersRef.current.delete(sendId);
    }, cleanupDelayMs);

    cleanupTimersRef.current.set(sendId, timer);
  }, [cleanupDelayMs]);

  const handleMarketingEmailSendEvent = useCallback((event: MarketingEmailSendStatusEvent) => {
    if (!event.send_id) {
      console.warn('Received marketing email send event without send_id:', event);
      return;
    }

    setActiveMarketingEmailSends(prev => {
      const next = new Map(prev);
      const existing = prev.get(event.send_id);

      // Accumulate progress messages across steps so the tooltip can show the
      // full sequence (matches the sync-tooltip behavior).
      if (event.status === 'progress' && existing && (existing.status === 'started' || existing.status === 'progress')) {
        const existingMessages: string[] = (existing as any).messages
          || (existing.progress?.message ? [existing.progress.message] : []);
        const existingStep = existing.progress?.step;
        const newStep = event.progress?.step;
        const newMessage = event.progress?.message;

        let messages: string[];
        if (newMessage) {
          if (newStep !== undefined && newStep === existingStep) {
            messages = [...existingMessages.slice(0, -1), newMessage];
          } else {
            messages = [...existingMessages, newMessage];
          }
        } else {
          messages = existingMessages;
        }

        (event as any).messages = messages;
      }

      next.set(event.send_id, event);
      return next;
    });

    if (event.status === 'accepted' || event.status === 'failed') {
      scheduleCleanup(event.send_id);
    }
  }, [scheduleCleanup]);

  useEffect(() => {
    return () => {
      for (const timer of cleanupTimersRef.current.values()) clearTimeout(timer);
      cleanupTimersRef.current.clear();
    };
  }, []);

  return { activeMarketingEmailSends, handleMarketingEmailSendEvent };
}
